/**
 * 앱 공통 유틸리티
 */
class AppUtil {
  private static debugMode: boolean = import.meta.env.DEV;

  /**
   * 디버그 모드 여부 반환
   */
  static getDebugMode(): boolean {
    return this.debugMode;
  }

  /**
   * 디버그 모드 설정
   */
  static setDebugMode(mode: boolean): void {
    this.debugMode = mode;
  }

  /**
   * 이메일 유효성 검사
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * 전화번호 유효성 검사
   */
  static isValidPhone(phone: string): boolean {
    const phoneRegex = /^[\d-]+$/;
    return phoneRegex.test(phone);
  }

  /**
   * 하이픈 제거
   */
  static removeHyphens(str: string): string {
    return str.replace(/-/g, '');
  }

  /**
   * 날짜 포맷팅
   */
  static formatDate(date: Date, format: string = 'YYYY-MM-DD'): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return format
      .replace('YYYY', String(year))
      .replace('MM', month)
      .replace('DD', day)
      .replace('HH', hours)
      .replace('mm', minutes)
      .replace('ss', seconds);
  }

  /**
   * 숫자 천단위 콤마 포맷팅
   */
  static formatNumber(num: number): string {
    return num.toLocaleString('ko-KR');
  }

  /**
   * 빈 값 체크
   */
  static isEmpty(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
  }

  /**
   * 딥 클론
   */
  static deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }

  /**
   * 전화번호 포맷팅 (010-1234-5678)
   */
  static formatPhoneNumber(phone: string): string {
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
    }
  }

  /**
   * 디바운스
   */
  static debounce<T extends (...args: unknown[]) => unknown>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout | null = null;
    return (...args: Parameters<T>) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  /**
   * 스로틀
   */
  static throttle<T extends (...args: unknown[]) => unknown>(
    func: T,
    limit: number
  ): (...args: Parameters<T>) => void {
    let inThrottle = false;
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }
}

export default AppUtil;
